import { useCallback, useEffect, useState } from 'react';
import { advancedCadTypes, cadTypes, getFileExtension, listContent } from '@/utils/fileUtils';
import { checkUploadedFiles, GeneralValidationResult } from '@/utils/fileValidation';
import { FileWithPath } from '@mantine/dropzone';

export type FileValidationErrors = {
  general?: GeneralValidationResult;
  files: Record<string, string[] | undefined>;
};

export const useFileValidation = (
  maxAllowedFileSize: number | undefined | null,
  hasCadSupport: boolean,
  hasAdvancedCadSupport: boolean,
  hasRapidPBRSupport: boolean,
  storageLeft: number,
  files: FileWithPath[],
  planImportCost?: number | null
) => {
  const [fileValidationErrors, setFileValidationErrors] = useState<FileValidationErrors>({ files: {} });

  const [pbrFiles, setPbrFiles] = useState<string[]>([]);

  const [CADFiles, setCADFiles] = useState<string[]>([]);

  const [isValidating, setIsValidating] = useState(false);

  const validateFiles = useCallback(async () => {
    setIsValidating(true);
    setFileValidationErrors((_) => ({ files: {} }));
    setPbrFiles([]);
    setCADFiles([]);

    const zipFiles = files.filter((file) => getFileExtension(file).toLowerCase() === 'zip');
    for (const file of zipFiles) {
      const content = await listContent(file);
      const maxFiles = content.filter((fileName) => fileName.endsWith('.max'));
      const zippedCADFiles = content.filter(
        (fileName) =>
          advancedCadTypes.some((type) => fileName.endsWith(type)) || cadTypes.some((type) => fileName.endsWith(type))
      );
      setCADFiles(zippedCADFiles);
      setPbrFiles(maxFiles);
    }

    const CADFiles = files
      .map((file) => file.name)
      .filter(
        (fileName) =>
          advancedCadTypes.some((type) => fileName.endsWith(type)) || cadTypes.some((type) => fileName.endsWith(type))
      );
    setCADFiles((zippedCADFiles) => [...zippedCADFiles, ...CADFiles]);

    const validationResult = await checkUploadedFiles({
      files,
      context: {
        rapidPbrActive: hasRapidPBRSupport,
        storageLeft,
        hasCadSupport,
        hasAdvancedCadSupport,
        maxAllowedFileSize,
        planImportCost,
      },
    });

    setFileValidationErrors(validationResult);
    setIsValidating(false);
  }, [
    files,
    maxAllowedFileSize,
    hasCadSupport,
    hasAdvancedCadSupport,
    hasRapidPBRSupport,
    storageLeft,
    planImportCost,
  ]);

  useEffect(() => {
    validateFiles();
  }, [validateFiles]);

  return {
    isValidating,
    fileValidationErrors,
    pbrFiles,
    CADFiles,
  };
};
